import type { Tag } from '@numistic/contracts'
import {
  useCreateTag,
  useRemoveSavedFilter,
  useRemoveTag,
  useSavedFilters,
  useTags,
  useUpdateTag,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Button, Card, CardContent, CardHeader, CardTitle, cn, Input } from '@numistic/ui'
import { createRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'

import { SpecimenList } from '../../collections/specimen-list.js'
import { MutationErrors } from '../../components/mutation-errors.js'
import { settingsRoute } from './layout.js'

export const settingsTagsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'tags',
  component: TagsPage,
})

/** Спокойная палитра меток: читается и на светлой, и на тёмной теме. */
const COLORS = ['#b45309', '#15803d', '#0369a1', '#7c3aed', '#be123c', '#475569']

function TagsPage() {
  const { t } = useTranslation()
  const tags = useTags()
  const create = useCreateTag()
  const update = useUpdateTag()
  const remove = useRemoveTag()
  const [name, setName] = useState('')
  const [shown, setShown] = useState<string | null>(null)
  const list = tags.data ?? []

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('tags.title')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('tags.hint')}</p>
        </CardHeader>
        <CardContent className="grid gap-4">
          {tags.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : list.length ? (
            <ul className="divide-y">
              {list.map((tag) => (
                <TagRow
                  key={`${tag.id}:${tag.name}:${tag.color}`}
                  tag={tag}
                  shown={shown === tag.id}
                  pending={update.isPending || remove.isPending}
                  onToggle={() => setShown((s) => (s === tag.id ? null : tag.id))}
                  onSave={(patch) => update.mutate({ id: tag.id, ...patch })}
                  onRemove={() => {
                    if (
                      window.confirm(t('tags.deleteConfirm', { name: tag.name, count: tag.count }))
                    )
                      remove.mutate(tag.id)
                  }}
                />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('tags.empty')}</p>
          )}
          <form
            className="flex gap-2 border-t pt-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (name.trim())
                create.mutate({ name: name.trim() }, { onSuccess: () => setName('') })
            }}
          >
            <Input
              aria-label={t('tags.new')}
              placeholder={t('tags.new')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button type="submit" variant="outline" disabled={create.isPending || !name.trim()}>
              {t('common.add')}
            </Button>
          </form>
          <MutationErrors errors={[create.error, update.error, remove.error]} />
        </CardContent>
      </Card>
      {shown ? <SpecimenList query={{ tagId: shown }} /> : null}
      <SavedViews />
    </div>
  )
}

function TagRow({
  tag,
  shown,
  pending,
  onToggle,
  onSave,
  onRemove,
}: {
  tag: Tag
  shown: boolean
  pending: boolean
  onToggle: () => void
  onSave: (patch: { name?: string; color?: string | null }) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(tag.name)
  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <span
        aria-hidden
        className="size-3 shrink-0 rounded-full border"
        style={{ background: tag.color ?? 'transparent' }}
      />
      <form
        className="flex min-w-48 flex-1 gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() && name.trim() !== tag.name) onSave({ name: name.trim() })
        }}
      >
        <Input
          aria-label={t('tags.rename', { name: tag.name })}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {name.trim() !== tag.name ? (
          <Button type="submit" size="sm" disabled={pending || !name.trim()}>
            {t('common.save')}
          </Button>
        ) : null}
      </form>
      <div className="flex items-center gap-1" role="group" aria-label={t('tags.color')}>
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={c}
            aria-pressed={tag.color === c}
            disabled={pending}
            onClick={() => onSave({ color: tag.color === c ? null : c })}
            className={cn(
              'size-5 rounded-full border-2 border-transparent',
              tag.color === c && 'border-foreground',
            )}
            style={{ background: c }}
          />
        ))}
      </div>
      <Button type="button" variant="ghost" size="sm" aria-pressed={shown} onClick={onToggle}>
        {t('tags.specimens', { count: tag.count })}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-destructive"
        disabled={pending}
        onClick={onRemove}
      >
        {t('common.remove')}
      </Button>
    </li>
  )
}

/** Сохранённые подборки (умные коллекции): открыть или удалить. */
function SavedViews() {
  const { t } = useTranslation()
  const views = useSavedFilters()
  const remove = useRemoveSavedFilter()
  const list = views.data ?? []
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('views.title')}</CardTitle>
        <p className="text-xs text-muted-foreground">{t('views.hint')}</p>
      </CardHeader>
      <CardContent className="grid gap-3">
        {list.length ? (
          <ul className="divide-y">
            {list.map((v) => (
              <li key={v.id} className="flex items-center gap-3 py-2.5">
                <Link
                  to="/views/$id"
                  params={{ id: v.id }}
                  className="mr-auto text-sm hover:text-primary hover:underline"
                >
                  {v.name}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {t('views.conditions', { count: v.filter.and.length })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(t('views.deleteConfirm', { name: v.name })))
                      remove.mutate(v.id)
                  }}
                >
                  {t('common.remove')}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('views.empty')}</p>
        )}
        <MutationErrors errors={[remove.error]} />
      </CardContent>
    </Card>
  )
}
